/**
 * GEN-4193 — regra única de datas do sistema (Brasília × UTC).
 *
 * Existem dois tipos de data, e cada um tem a sua leitura:
 *
 * 1. MOMENTO (criado em, histórico, envio, "agora"): é um instante em UTC. O "dia" de um momento é
 *    SEMPRE o dia de Brasília (America/Sao_Paulo), calculado de forma explícita aqui — nunca pelo
 *    UTC (`toISOString().slice(0, 10)` / `moment.utc()`), que vira o dia às 21h de Brasília, nem
 *    pelo fuso do navegador (`setHours(0, 0, 0, 0)`, `moment().startOf(...)`).
 *
 * 2. DATA DE CALENDÁRIO (vencimento, validade, emissão): vem da API como meia-noite UTC
 *    (`AAAA-MM-DDT00:00:00.000Z`; há legado às 03:00Z). NUNCA converter fuso: exibir com
 *    `timeZone: "UTC"` e extrair o dia com `toISOString().slice(0, 10)` — para ESTE tipo isso está
 *    certo. Ao enviar uma data de calendário escolhida pelo usuário, mandar `AAAA-MM-DD`, nunca a
 *    meia-noite local convertida (que vira 03:00Z ou o dia anterior).
 *
 * Decisão de produto: a certidão vale até 23:59 de Brasília do dia do vencimento — ou seja, ela só
 * está vencida a partir do dia SEGUINTE ao vencimento (no dia de Brasília).
 *
 * Os seletores de data (react-day-picker, DateRange) trabalham com `Date` em meia-noite LOCAL do dia
 * escolhido: o dia do picker são os componentes locais (`getFullYear/getMonth/getDate`), e é por isso
 * que `pickerDateToISODate` lê o local, e não o UTC.
 */

import moment, { type Moment } from "moment";

export const BRASILIA_TIME_ZONE = "America/Sao_Paulo";

/** Data aceita pelos helpers: Date, ISO string ou epoch em ms. */
export type DateInput = Date | string | number;

export type DayParts = {
  year: number;
  /** 1–12 */
  month: number;
  day: number;
};

type InstantParts = DayParts & { hour: number; minute: number; second: number };

let brasiliaFormatter: Intl.DateTimeFormat | null = null;

const getBrasiliaFormatter = () => {
  if (!brasiliaFormatter) {
    brasiliaFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: BRASILIA_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
  }
  return brasiliaFormatter;
};

const toDate = (value: DateInput): Date => (value instanceof Date ? value : new Date(value));

const pad = (n: number, width = 2) => String(n).padStart(width, "0");

const partsToISODate = ({ year, month, day }: DayParts) => `${pad(year, 4)}-${pad(month)}-${pad(day)}`;

const brasiliaInstantParts = (instant: Date): InstantParts => {
  const parts = getBrasiliaFormatter().formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    // Alguns motores devolvem "24" para meia-noite mesmo com h23.
    hour: get("hour") % 24,
    minute: get("minute"),
    second: get("second"),
  };
};

/** Diferença (ms) entre o relógio de Brasília e o UTC num instante (hoje: -3h). */
const brasiliaOffsetMs = (instant: Date): number => {
  const p = brasiliaInstantParts(instant);
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUTC - Math.floor(instant.getTime() / 1000) * 1000;
};

/**
 * Instante em que começa (00:00 de Brasília) o dia de calendário informado. `day`/`month` podem
 * transbordar (dia 32, mês 13) — o Date.UTC normaliza.
 */
const brasiliaMidnight = (year: number, month: number, day: number): Date => {
  const asUTC = Date.UTC(year, month - 1, day);
  const first = asUTC - brasiliaOffsetMs(new Date(asUTC));
  // Segunda passada: cobre a troca de offset (horário de verão histórico) entre o palpite e o alvo.
  const second = asUTC - brasiliaOffsetMs(new Date(first));
  // Quando o horário de verão começava à meia-noite, a meia-noite não existia (o relógio pulava para
  // 01:00) e a segunda passada cai no dia anterior. O dia começa então na própria virada, que é a
  // primeira passada (ex.: 04/11/2018 começa às 03:00Z).
  const target = new Date(asUTC).toISOString().slice(0, 10);
  return brasiliaISODate(second) === target ? new Date(second) : new Date(first);
};

// ---------------------------------------------------------------------------
// MOMENTO → dia de Brasília
// ---------------------------------------------------------------------------

/** Ano, mês (1–12) e dia de Brasília de um instante (padrão: agora). */
export const brasiliaDateParts = (instant: DateInput = Date.now()): DayParts => {
  const { year, month, day } = brasiliaInstantParts(toDate(instant));
  return { year, month, day };
};

/** Dia de Brasília (`AAAA-MM-DD`) de um instante. */
export const brasiliaISODate = (instant: DateInput): string => partsToISODate(brasiliaDateParts(instant));

/** Hoje em Brasília, `AAAA-MM-DD`. Use no lugar de `new Date().toISOString().slice(0, 10)`. */
export const todayBrasilia = (): string => brasiliaISODate(Date.now());

/** Instante de 00:00 de Brasília do dia (de Brasília) do instante informado. */
export const startOfDayBrasilia = (instant: DateInput = Date.now()): Date => {
  const { year, month, day } = brasiliaDateParts(instant);
  return brasiliaMidnight(year, month, day);
};

/** Último ms (23:59:59.999 de Brasília) do dia (de Brasília) do instante informado. */
export const endOfDayBrasilia = (instant: DateInput = Date.now()): Date => {
  const { year, month, day } = brasiliaDateParts(instant);
  return new Date(brasiliaMidnight(year, month, day + 1).getTime() - 1);
};

/** Instante de 00:00 de Brasília do dia 1º do mês (de Brasília) do instante informado. */
export const startOfMonthBrasilia = (instant: DateInput = Date.now()): Date => {
  const { year, month } = brasiliaDateParts(instant);
  return brasiliaMidnight(year, month, 1);
};

/** Último ms (23:59:59.999 de Brasília) do último dia do mês (de Brasília) do instante informado. */
export const endOfMonthBrasilia = (instant: DateInput = Date.now()): Date => {
  const { year, month } = brasiliaDateParts(instant);
  return new Date(brasiliaMidnight(year, month + 1, 1).getTime() - 1);
};

/** Primeiro e último dia (`AAAA-MM-DD`) do mês de Brasília do instante informado (padrão: agora). */
export const monthRangeBrasilia = (instant: DateInput = Date.now()): { start: string; end: string } => {
  const { year, month } = brasiliaDateParts(instant);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    start: partsToISODate({ year, month, day: 1 }),
    end: partsToISODate({ year, month, day: lastDay }),
  };
};

// ---------------------------------------------------------------------------
// DATA DE CALENDÁRIO (sem fuso)
// ---------------------------------------------------------------------------

/** Soma (ou subtrai) dias a uma data de calendário `AAAA-MM-DD`. Aritmética pura, sem fuso. */
export const addDaysISODate = (isoDate: string, days: number): string => {
  const [year, month, day] = isoDate.slice(0, 10).split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return partsToISODate({
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  });
};

/**
 * Formata uma data de calendário vinda da API (meia-noite UTC ou legado 03:00Z) em `DD/MM/AAAA`
 * SEM converter fuso. Vazio/ inválido → `fallback` (padrão "").
 */
export const formatCalendarDate = (value: DateInput | null | undefined, fallback = ""): string => {
  if (value === null || value === undefined || value === "") return fallback;
  const date = toDate(value);
  if (isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

/** Extrai o `AAAA-MM-DD` de uma data de calendário vinda da API (lida em UTC, sem converter fuso). */
export const calendarDateToISODate = (value: DateInput | null | undefined): string | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const date = toDate(value);
  if (isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
};

// ---------------------------------------------------------------------------
// Seletor de data (picker) ↔ AAAA-MM-DD
// ---------------------------------------------------------------------------

/**
 * Data escolhida no picker (Date em meia-noite LOCAL do dia escolhido) → `AAAA-MM-DD`. Lê os
 * componentes locais: é o dia que o usuário clicou, em qualquer fuso de navegador. Substitui o
 * `date.toISOString().slice(0, 10)`, que devolve o dia SEGUINTE quando o Date não é meia-noite
 * (fim do dia/mês às 23:59:59 locais, "agora" depois das 21h de Brasília).
 */
export function pickerDateToISODate(date: Date): string;
export function pickerDateToISODate(date: Date | null | undefined): string | undefined;
export function pickerDateToISODate(date: Date | null | undefined): string | undefined {
  if (!date || isNaN(date.getTime())) return undefined;
  return partsToISODate({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });
}

/** `AAAA-MM-DD` → Date em meia-noite LOCAL desse dia, no formato que o picker espera. */
export const isoDateToPickerDate = (isoDate: string): Date => {
  const [year, month, day] = isoDate.slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day);
};

/** Hoje (dia de Brasília) como Date do picker. */
export const todayBrasiliaPickerDate = (): Date => isoDateToPickerDate(todayBrasilia());

/**
 * Hoje (dia de Brasília) como `moment` LOCAL em meia-noite — base para montar PERÍODOS PADRÃO do picker
 * (`todayBrasiliaMoment().subtract(3, "month").startOf("month").toDate()`). Use no lugar de `moment()`
 * nesses padrões: com o navegador fora de Brasília (ex.: UTC às 01h de 01/11), `moment()` já é novembro
 * enquanto em Brasília ainda é 31/10.
 */
export const todayBrasiliaMoment = (): Moment => moment(todayBrasiliaPickerDate());

/**
 * Dia de Brasília de um MOMENTO (createdAt etc.) como Date do picker. Vazio/inválido → hoje de Brasília
 * (mesmo comportamento do `moment(undefined)` que substitui).
 */
export const instantToBrasiliaPickerDate = (instant: DateInput | null | undefined): Date => {
  if (instant === null || instant === undefined || instant === "") return todayBrasiliaPickerDate();
  const date = toDate(instant);
  if (isNaN(date.getTime())) return todayBrasiliaPickerDate();
  return isoDateToPickerDate(brasiliaISODate(date));
};
